(() => {
  if (window.StlModalVariant) return;

  const getInjectedModal = () =>
    window.__stlModalSectionDeps?.getInjectedModal?.() ?? null;

  const parseJsonScript = (modal, selector) => {
    const el = modal?.querySelector(selector);
    if (!el?.textContent?.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const normOpt = v => String(v ?? '').trim();

  const padOptionsToThree = options => {
    const padded = [...options.map(normOpt)];
    while (padded.length < 3) padded.push('');
    return padded;
  };

  const resolveStlVariantForSize = (modal, sizeValue) => {
    const sizesEl = modal?.querySelector(
      'shop-look-sizes[data-stl-size-option-index]'
    );
    const rawIdx = sizesEl?.dataset?.stlSizeOptionIndex;
    const idx = rawIdx === '' || rawIdx == null ? -1 : Number(rawIdx);
    if (!Number.isFinite(idx) || idx < 0 || sizeValue == null) return null;
    const variants = parseJsonScript(modal, 'script[data-stl-variants]');
    const baseOpts = parseJsonScript(
      modal,
      'script[data-stl-base-variant-options]'
    );
    if (!Array.isArray(variants) || !Array.isArray(baseOpts)) return null;
    const want = padOptionsToThree(baseOpts);
    want[idx] = normOpt(sizeValue);
    return (
      variants.find(v => {
        const padded = padOptionsToThree(v?.options ?? []);
        return (
          padded[0] === want[0] &&
          padded[1] === want[1] &&
          padded[2] === want[2]
        );
      }) ?? null
    );
  };

  const stlJsonInventoryToBool = v =>
    v === true || v === 'true' || v === 1 || v === '1';

  const getStlModalLabels = () => {
    const fallbackAdd = window.productsProductAddToCartLabel;
    const w = window.stlModalButtonLabels;
    if (w && typeof w === 'object') {
      const add =
        typeof w.add === 'string' && w.add.length > 0
          ? w.add
          : typeof fallbackAdd === 'string'
            ? fallbackAdd
            : '';
      return {
        add,
        preorder: typeof w.preorder === 'string' ? w.preorder : '',
        notify: typeof w.notify === 'string' ? w.notify : '',
        oos: typeof w.oos === 'string' ? w.oos : ''
      };
    }
    return {
      add: typeof fallbackAdd === 'string' ? fallbackAdd : '',
      preorder: '',
      notify: '',
      oos:
        typeof outOfStockGlobalVariable !== 'undefined'
          ? outOfStockGlobalVariable
          : ''
    };
  };

  const findAddButtonTextEl = addBtn => {
    if (!addBtn) return null;
    const direct = addBtn.querySelector('.product-form__button-text');
    if (direct) return direct;
    for (const el of addBtn.querySelectorAll(':scope > span')) {
      if (!el.closest('.loading-overlay__spinner')) return el;
    }
    return null;
  };

  const updateStlPriceDisplay = (modal, variant) => {
    if (!modal || !variant) return;
    const sale = modal.querySelector('.price-item--sale .money');
    const compareDd = modal.querySelector('.price__compare');
    const compareMoney = modal.querySelector('.price-item--regular .money');
    const fmt =
      typeof window.formatMoney === 'function'
        ? cents => window.formatMoney(cents)
        : cents => (Number(cents) / 100).toFixed(2);

    if (sale) sale.textContent = fmt(variant.price);

    const cmp = Number(variant.compare_at_price) || 0;
    const showCompare = cmp > Number(variant.price);
    if (compareDd) compareDd.classList.toggle('hidden', !showCompare);
    if (compareMoney) {
      compareMoney.textContent = showCompare ? fmt(cmp) : '';
      compareMoney.parentElement?.classList.toggle('hidden', !showCompare);
    }
    const saleSpan = sale?.closest('.price-item--sale');
    if (saleSpan) saleSpan.classList.toggle('sale-red', showCompare);
  };

  // Read pre-order only from modal-scoped DOM: window.variantsPreOrderInsteadOos and
  // window.productBadgePreOrder describe the PAGE product, not the one the modal renders.
  // The radios carry data-stl-pre-order, not the PDP's data-pre-order, because global.js and
  // sticky-quick-add.liquid read data-pre-order document-wide with no container scoping.
  const isStlSizeInputPreOrder = (modal, input, variantFromJson) => {
    if (
      typeof variantFromJson === 'object' &&
      variantFromJson !== null &&
      'pre_order' in variantFromJson
    ) {
      return variantFromJson.pre_order === true;
    }

    if (input?.dataset.stlPreOrder !== undefined) {
      return input.dataset.stlPreOrder === 'true';
    }

    if (modal?.dataset.stlVariantPreOrderFlag !== 'true') return false;

    return (
      input?.dataset.inventoryPolicy === 'continue' &&
      Number(input?.dataset.inventoryQuantity) <= 0
    );
  };

  const applyStlSizeSelectionFromInput = input => {
    const modal = getInjectedModal();
    if (!modal?.classList.contains('active')) return;
    const form = modal.querySelector('.shop-the-look__form');
    const sizeTitle = modal.querySelector('.size-title');
    const addBtn =
      modal.querySelector('#addProduct') ??
      form?.querySelector('.shop-the-look__button[type="submit"]') ??
      form?.querySelector('.shop-the-look__button');
    const variantInput = form?.querySelector('.product-variant-id');
    if (!form || !variantInput) return;

    const item = input.closest('.item');
    const label = item?.querySelector('label');
    const sizeLabelPlain =
      label?.querySelector('.stl-size-option-text')?.textContent.trim() ??
      label?.textContent.replace(/\s+/g, ' ').trim() ??
      '';

    const rawInputVid =
      input.dataset.variantId || input.getAttribute('data-variant-id') || '';
    const inputVariantIdFromDom =
      typeof rawInputVid === 'string' ? rawInputVid.trim() : '';
    const domAvailable =
      input.dataset.available === 'true' ||
      label?.dataset.available === 'true' ||
      label?.getAttribute('available') === 'true';

    const variantsJson = parseJsonScript(modal, 'script[data-stl-variants]');
    const resolved = resolveStlVariantForSize(modal, input.value);

    /** Prefer Liquid-rendered `data-variant-id` + JSON (same source as size row). `resolved` can mismatch when base options / option order diverge. */
    let variantForUi = null;
    if (inputVariantIdFromDom && Array.isArray(variantsJson)) {
      variantForUi =
        variantsJson.find(
          v => String(v?.id) === String(inputVariantIdFromDom)
        ) ?? null;
    }
    if (!variantForUi) {
      variantForUi = resolved;
    }

    const productPreorder = modal.dataset.stlPreorder === 'true';
    const variantPreorder = isStlSizeInputPreOrder(modal, input, variantForUi);
    const preorder = productPreorder || variantPreorder;

    let variantId = '';
    let price = Number(input.dataset.price);
    let compareAtPrice = Number(input.dataset.compareAtPrice);
    let inventoryAvailable = false;

    const sizesEl = modal.querySelector(
      'shop-look-sizes[data-stl-size-option-index]'
    );
    const rawSizeIdx = sizesEl?.dataset?.stlSizeOptionIndex;
    const sizeOptIdx =
      rawSizeIdx === '' || rawSizeIdx == null ? -1 : Number(rawSizeIdx);

    if (variantForUi) {
      variantId = String(variantForUi.id);
      price = Number(variantForUi.price);
      compareAtPrice = Number(variantForUi.compare_at_price) || 0;
      inventoryAvailable = stlJsonInventoryToBool(
        variantForUi.inventory_available
      );
    } else if (inputVariantIdFromDom) {
      variantId = inputVariantIdFromDom;
      inventoryAvailable = domAvailable;
    }

    if (variantId) variantInput.value = variantId;

    const {
      add: lblAdd,
      preorder: lblPreorder,
      notify: lblNotify,
      oos: lblOos
    } = getStlModalLabels();

    const oosSuffix =
      lblOos && String(lblOos).trim() ? ` · ${String(lblOos).trim()}` : '';

    const sizeDisplayFromVariant =
      variantForUi &&
      Number.isFinite(sizeOptIdx) &&
      sizeOptIdx >= 0 &&
      Array.isArray(variantForUi.options)
        ? normOpt(variantForUi.options[sizeOptIdx])
        : '';
    const sizeDisplay =
      sizeDisplayFromVariant || normOpt(input.value) || sizeLabelPlain;

    if (sizeTitle && sizeDisplay) {
      sizeTitle.textContent = inventoryAvailable
        ? sizeDisplay
        : `${sizeDisplay}${oosSuffix}`;
    }

    const ctaButtons = [
      addBtn,
      ...modal.querySelectorAll('.shop-the-look__button.bottom-btn')
    ].filter(Boolean);

    ctaButtons.forEach(btn => {
      btn.classList.remove('notify');
      btn.removeAttribute('disabled');
      const textEl = findAddButtonTextEl(btn);
      if (inventoryAvailable && preorder == false) {
        const addText = lblAdd || window.productsProductAddToCartLabel || '';
        if (textEl && addText) textEl.textContent = addText;
      } else if (preorder) {
        if (textEl && lblPreorder) textEl.textContent = lblPreorder;
      } else {
        if (textEl && lblNotify) textEl.textContent = lblNotify;
        btn.classList.add('notify');
      }
    });

    modal
      .querySelectorAll(
        '.js-stl-pre-order-label, .js-stl-pre-order-description'
      )
      .forEach(el => {
        el.hidden = !(variantPreorder && el.childElementCount > 0);
      });

    /** WDM is rendered as a superset (any variant blocked) and hidden per variant. Missing JSON key or unresolved variant fails toward showing the warning. */
    const wdmProductWarns = modal.dataset.stlWdmProduct === 'true';
    const wdmVariantBlocked =
      typeof variantForUi === 'object' &&
      variantForUi !== null &&
      'wdm_blocked' in variantForUi
        ? variantForUi.wdm_blocked === true
        : true;
    modal.querySelectorAll('.warning-delivery-message').forEach(el => {
      el.classList.toggle(
        'stl-wdm-hidden',
        !(wdmProductWarns || wdmVariantBlocked)
      );
    });

    const addProductLink = form?.querySelector('.addProduct');
    if (addProductLink) {
      addProductLink.classList.toggle('hidden', !inventoryAvailable);
    }

    if (variantId && Number.isFinite(price)) {
      updateStlPriceDisplay(modal, {
        price,
        compare_at_price: compareAtPrice
      });
    }
  };

  const syncStlFormFromCheckedSize = modal => {
    const checked = modal?.querySelector(
      '.shop-look__modal-sizes input[type="radio"]:checked'
    );
    if (checked instanceof HTMLInputElement) {
      applyStlSizeSelectionFromInput(checked);
    }
  };

  /** Baseline for `product_size_change` — updated on open/swap and after each user size pick. */
  const syncStlLastSizeDataset = modal => {
    if (!modal) return;
    const checked = modal.querySelector(
      '.shop-look__modal-sizes input[type="radio"]:checked'
    );
    modal.dataset.stlLastSizeSelection =
      checked instanceof HTMLInputElement ? String(checked.value ?? '') : '';
  };

  window.StlModalVariant = {
    parseJsonScript,
    normOpt,
    padOptionsToThree,
    resolveStlVariantForSize,
    stlJsonInventoryToBool,
    getStlModalLabels,
    findAddButtonTextEl,
    updateStlPriceDisplay,
    isStlSizeInputPreOrder,
    applyStlSizeSelectionFromInput,
    syncStlFormFromCheckedSize,
    syncStlLastSizeDataset
  };
})();
